// Locate-me feedback: pending, found, and the three ways a fix can fail (#438).
//
// `handleLocateMe` used to be `navigator.geolocation.getCurrentPosition(success)` — a success
// callback and nothing else. No error callback, no `timeout`, and no state that changes before
// the fix lands. Measured on a production build of `03f5d39` at 390x844: with
// `getCurrentPosition` replaced by a function that never calls back, the button looked exactly
// as it had before the tap, forever; and denying the permission produced `errors=[]` — the
// browser refused and the visitor was told nothing. A tap that is heard and a tap that is
// dropped were indistinguishable.
//
// The request lives here rather than inline in src/App.js so the pending / found / failure
// transitions can be unit-tested against a stub `geolocation` — none of this is reachable from
// the jsdom suite, which cannot mount MapScreen (it needs a real Google Maps instance).
//
// The owner is colourblind, so every state this module names must be renderable as a SHAPE or as
// WORDS. That is why the failure states carry sentences rather than a severity level: the caller
// has something to say, not a hue to pick.

export const GEO_IDLE = 'idle';
export const GEO_PENDING = 'pending';
export const GEO_FOUND = 'found';
export const GEO_DENIED = 'denied';
export const GEO_TIMEOUT = 'timeout';
export const GEO_UNAVAILABLE = 'unavailable';

// Without `timeout` the spec lets a request stay pending indefinitely, which is exactly the
// never-resolving case the issue measured. 12s is long enough for a cold GPS fix on a phone and
// short enough that a visitor has not already decided the button is broken.
// `maximumAge` accepts a fix up to a minute old: locate-me answers "roughly where am I on this
// map", and re-acquiring for that is a slower answer to the same question.
export const GEO_REQUEST_OPTIONS = Object.freeze({
  enableHighAccuracy: false,
  timeout: 12000,
  maximumAge: 60000,
});

// The failure notice clears itself. It is advice, not an error the visitor must dismiss, and a
// notice that needs a tap to remove would be a second dead control on the same screen.
export const GEO_NOTICE_MS = 7000;

// The three PositionError codes are fixed by the spec: 1 PERMISSION_DENIED,
// 2 POSITION_UNAVAILABLE, 3 TIMEOUT. Anything else — a missing error object, a browser inventing
// a fourth code — is reported as unavailable rather than guessed at: "we could not get a fix" is
// true of every unknown case, while "you denied it" would be an accusation we cannot support.
export function geolocationFailureStatus(error) {
  const code = error && typeof error.code === 'number' ? error.code : null;
  if (code === 1) return GEO_DENIED;
  if (code === 3) return GEO_TIMEOUT;
  return GEO_UNAVAILABLE;
}

export function isGeolocationFailure(status) {
  return status === GEO_DENIED || status === GEO_TIMEOUT || status === GEO_UNAVAILABLE;
}

// Every string a visitor can be shown, in both languages, in one table. The failure sentences say
// what happened and what the visitor can do about it; none of them blames the visitor, and none
// of them needs a colour to be read. `denied` deliberately says "for this site" rather than
// "you denied it" — a browser-level block, a site-level block and an OS-level block all arrive
// as code 1, and the visitor's fix is the same in every case.
const GEO_MESSAGES = Object.freeze({
  [GEO_PENDING]: Object.freeze({
    en: 'Finding you…',
    ar: '…جارٍ تحديد موقعك',
  }),
  [GEO_DENIED]: Object.freeze({
    en: 'Location is off for this site. Turn it on in your browser settings.',
    ar: 'خدمة الموقع مغلقة لهذا الموقع. يمكنك تفعيلها من إعدادات المتصفح.',
  }),
  [GEO_TIMEOUT]: Object.freeze({
    en: 'Could not find you in time. Try again.',
    ar: 'تعذّر تحديد موقعك في الوقت المناسب. حاول مرة أخرى.',
  }),
  // Deliberately "right now" rather than "on this device": this one string covers a device with
  // no geolocation at all, the spec's POSITION_UNAVAILABLE, a browser that threw, and a map host
  // that is not ready to be moved. All four are true statements of "not available right now",
  // and none of them is something the visitor can be told to go and fix.
  [GEO_UNAVAILABLE]: Object.freeze({
    en: 'Location is not available right now.',
    ar: 'تحديد الموقع غير متاح حالياً.',
  }),
});

// `idle` and `found` return null on purpose: the map itself is the feedback once a fix lands
// (it pans, it zooms, the blue dot appears), so a sentence saying so would be noise.
export function geolocationMessage(status) {
  return GEO_MESSAGES[status] || null;
}

// One request, one place. The caller supplies the map work (`onPosition`) and the feedback
// (`onStatus`); everything about how a fix is asked for and how a failure is classified is here.
//
// `onStatus(GEO_PENDING)` is emitted BEFORE the call, so the spinner is on screen while the
// browser's own permission prompt is up — that prompt can sit there for as long as the visitor
// takes to read it.
export function requestVisitorLocation({ geolocation, onStatus, onPosition }) {
  const report = typeof onStatus === 'function' ? onStatus : () => {};
  const deliver = typeof onPosition === 'function' ? onPosition : () => {};

  if (!geolocation || typeof geolocation.getCurrentPosition !== 'function') {
    // The old code returned silently here. A device with no geolocation at all is the one case
    // where the visitor cannot fix anything, and it is still better to say so than to leave a
    // button that does nothing.
    report(GEO_UNAVAILABLE);
    return;
  }

  report(GEO_PENDING);
  try {
    geolocation.getCurrentPosition(
      position => {
        // The map moves first, so the spinner is replaced by a screen that has already changed.
        deliver(position);
        report(GEO_FOUND);
      },
      error => report(geolocationFailureStatus(error)),
      GEO_REQUEST_OPTIONS,
    );
  } catch (error) {
    // Some browsers throw synchronously rather than calling back — an insecure origin is the
    // usual one. A throw here would leave the button stuck on `pending` forever, which is the
    // exact silence this ticket exists to remove.
    report(GEO_UNAVAILABLE);
  }
}
